import {QueryKey, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import api from "@/lib/api";
import {normalizePage} from "@/lib/paging.ts";
import {bannerKeys} from "@/hooks/useBanners.ts";

// ───────────────────────────────────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────────────────────────────────
export type UserId = string;
export type Role = "ADMIN" | "EDITOR";

export type User = {
    id: UserId;
    name: string;
    email: string;
    roles: Role[];
    enabled: boolean;
    avatarUrl?: string | null;
};

export type SaveUserInput = {
    id?: UserId;
    name: string;
    email: string;
    roles: Role[];
    enabled: boolean;
    avatarUrl?: string;
    password?: string;
};

export type SortField = "name" | "email" | "roles";
export type SortOrder = "asc" | "desc";

export type HookParams = {
    page?: number;
    size?: number;
    sortBy?: SortField;
    sortOrder?: SortOrder;
};

export type UserPage = ReturnType<typeof normalizePage<User>>;

// ───────────────────────────────────────────────────────────────────────────────
// Constants
// ───────────────────────────────────────────────────────────────────────────────
const ADMIN_BASE = "/api/v1/admin/users";

const DEFAULTS = {
    page: 0,
    size: 10,
    sortBy: "title" as SortField,
    sortOrder: "desc" as SortOrder,
};

// ───────────────────────────────────────────────────────────────────────────────
// Query Keys
// ───────────────────────────────────────────────────────────────────────────────
export const userKeys = {
    all: ["users"] as const,
    list: (params?: HookParams) => [...userKeys.all, "list", params ?? {}] as const,
};

// ───────────────────────────────────────────────────────────────────────────────
// Fetchers
// ───────────────────────────────────────────────────────────────────────────────
function emptyPage(page = DEFAULTS.page, size = DEFAULTS.size): UserPage {
    return normalizePage<User>({page, size, total: 0, content: []});
}

async function fetchUsers(params: HookParams = {}): Promise<UserPage> {
    const page = params.page ?? DEFAULTS.page;
    const size = params.size ?? DEFAULTS.size;
    const sortBy = params.sortBy ?? DEFAULTS.sortBy;
    const sortOrder = params.sortOrder ?? DEFAULTS.sortOrder;

    const sort = `${sortBy},${sortOrder}`;
    const {data} = await api.get(ADMIN_BASE, {params: {page, size, sort}});

    if (Array.isArray(data?.content)) return normalizePage<User>(data);
    if (Array.isArray(data?.items) && typeof data?.total === "number") return data;
    return emptyPage(page, size);
}

// ───────────────────────────────────────────────────────────────────────────────
// Queries
// ───────────────────────────────────────────────────────────────────────────────
export function useUsers(params?: HookParams) {
    const q: HookParams = {
        page: params?.page ?? DEFAULTS.page,
        size: params?.size ?? DEFAULTS.size,
        sortBy: params?.sortBy ?? DEFAULTS.sortBy,
        sortOrder: params?.sortOrder ?? DEFAULTS.sortOrder,
    };

    return useQuery({
        queryKey: userKeys.list(q),
        queryFn: () => fetchUsers(q),
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });
}

// ───────────────────────────────────────────────────────────────────────────────
// Mutations
// ───────────────────────────────────────────────────────────────────────────────
export function useSaveUser() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...userKeys.all, "save"],
        mutationFn: async (payload: SaveUserInput): Promise<User> => {
            const body = {
                name: payload.name?.trim(),
                email: payload.email?.trim(),
                roles: payload.roles,
                enabled: payload.enabled,
                avatarUrl: payload.avatarUrl?.trim() || undefined,
                ...(payload.id ? {} : payload.password ? {password: payload.password} : {}),
            };

            if (payload.id) {
                const {data} = await api.put(`${ADMIN_BASE}/${payload.id}`, body);
                return data;
            } else {
                const {data} = await api.post(ADMIN_BASE, body);
                return data;
            }
        },
        onSuccess: () => {
            qc.invalidateQueries({queryKey: userKeys.all});
        },
    });
}

export function useDeleteUser() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...userKeys.all, "delete"],
        mutationFn: async (id: UserId) => {
            await api.delete(`${ADMIN_BASE}/${id}`);
            return id;
        },
        onMutate: async (id) => {
            await qc.cancelQueries({queryKey: userKeys.all});

            const snapshots = qc.getQueriesData<unknown>({queryKey: userKeys.all});

            snapshots.forEach(([key, value]) => {
                const page = value as Partial<UserPage> | undefined;
                if (page && Array.isArray(page.items)) {
                    const next: UserPage = {
                        ...(page as UserPage),
                        items: page.items.filter((b: any) => b.id !== id),
                        total: Math.max(0, (page.total as number) - 1),
                    };
                    qc.setQueryData(key, next);
                }
            });

            return {snapshots};
        },
        onError: (_err, _id, ctx) => {
            ctx?.snapshots?.forEach(([key, value]: [unknown, unknown]) => {
                qc.setQueryData(<QueryKey>key, value);
            });
        },
        onSettled: () => {
            qc.invalidateQueries({queryKey: bannerKeys.all});
        },
    });
}

// export function useResetUserPassword() {
//     return useMutation({
//         mutationKey: ["admin", "users", "reset-password"],
//         mutationFn: async (id: UserId) => {
//             await api.post(`${ADMIN_BASE}/${id}/reset-password`);
//         },
//         onSuccess: () => notify.success("Password reset email sent"),
//         onError: (e) => notify.error(getErrMsg(e, "Could not reset password.")),
//     });
// }
